import { create } from 'zustand'

/**
 * Cross-step checkout state — the values the individual steps collect but that outlive them,
 * gathered in one place so the final `TradeInCreate` on the last step has everything it needs:
 *   - termsAccepted — the Details-step T&C tick. Starts (and is reset) unchecked; you can't leave
 *                     Details until it's on.
 *   - stories       — one optional free-text story per basket item, keyed by the potential's `_id`
 *                     so a story lives *next to* its product. When the product leaves the basket
 *                     (removed or deselected) its story is dropped — see `pruneStories`.
 *   - storyConsent  — permission to share the stories. Only required to leave Story when at least
 *                     one story is written (mirrors v1's CommentsForm gate).
 *   - carrierId     — the chosen `BrandCarrier` (`brandCarrierId` for `TradeInCreate`).
 *
 * Kept out of component state (zustand, like auth/selection) because the shared FlowLayout reads
 * these to gate Continue and the final submit reads them too — they aren't owned by one page.
 */
interface CheckoutState {
  termsAccepted: boolean
  stories: Record<string, string>
  storyConsent: boolean
  carrierId: string | null
  /** Whether the Details address form currently passes validation (drives Continue). */
  detailsValid: boolean
  /** The created trade-in id (set on submit), read by the Confirm step to poll its label. */
  tradeInId: string | null
  /** Basket reward (shop currency) and item count, snapshotted at submit — the potentials are
   *  consumed by TradeInCreate, so the Confirm step reads these instead of the (now empty) basket. */
  submittedReward: number
  submittedCount: number
  /**
   * An async action a step registers to run when Continue is pressed, before navigating — e.g. the
   * Details step saves the address here. Resolve `true` to proceed, `false` to stay on the step.
   * `null` when the current step has nothing to do before advancing.
   */
  onBeforeNext: (() => Promise<boolean>) | null
  setTermsAccepted: (accepted: boolean) => void
  setStory: (potentialId: string, text: string) => void
  setStoryConsent: (consent: boolean) => void
  setCarrierId: (id: string | null) => void
  setDetailsValid: (valid: boolean) => void
  setOnBeforeNext: (fn: (() => Promise<boolean>) | null) => void
  /** Record the successful submit: the new trade-in id and the basket snapshot for the Confirm step. */
  setTradeInResult: (id: string, reward: number, count: number) => void
  /** Keep only stories whose product is still in the basket; the rest are lost with their product. */
  pruneStories: (validIds: string[]) => void
  reset: () => void
}

export const useCheckoutStore = create<CheckoutState>((set) => ({
  termsAccepted: false,
  stories: {},
  storyConsent: false,
  carrierId: null,
  detailsValid: false,
  tradeInId: null,
  submittedReward: 0,
  submittedCount: 0,
  onBeforeNext: null,
  setTermsAccepted: (accepted) => set({ termsAccepted: accepted }),
  setStory: (potentialId, text) => set((s) => ({ stories: { ...s.stories, [potentialId]: text } })),
  setStoryConsent: (consent) => set({ storyConsent: consent }),
  setCarrierId: (id) => set({ carrierId: id }),
  setDetailsValid: (valid) => set({ detailsValid: valid }),
  setOnBeforeNext: (fn) => set({ onBeforeNext: fn }),
  setTradeInResult: (id, reward, count) =>
    set({ tradeInId: id, submittedReward: reward, submittedCount: count }),
  pruneStories: (validIds) =>
    set((s) => {
      const valid = new Set(validIds)
      const kept = Object.entries(s.stories).filter(([id]) => valid.has(id))
      if (kept.length === Object.keys(s.stories).length) return s // nothing removed
      return { stories: Object.fromEntries(kept) }
    }),
  reset: () =>
    set({
      termsAccepted: false,
      stories: {},
      storyConsent: false,
      carrierId: null,
      detailsValid: false,
      tradeInId: null,
      submittedReward: 0,
      submittedCount: 0,
      onBeforeNext: null,
    }),
}))
